/**
 * Bridge from the notification WebSocket (`SocketProvider`) to the AI chat for `chat.updated`.
 *
 * The chat socket only ever replies to the connection that sent a message, so a turn typed on one
 * device is invisible on another, and a turn the agent runs on its own is invisible everywhere. The
 * notification socket fans out per user, so the agent announces "conversation X changed" there and
 * every open client refetches.
 *
 * Deliberately a hint, not data — the payload carries no message content, and the client refetches
 * the conversation. A duplicate or out-of-order event is therefore harmless, which is what lets the
 * emitter be best-effort. Mirrors the `jobStatus` bridge so the two live side by side.
 */

export const CHAT_UPDATED_EVENT = 'omics:chat-updated'

export interface ChatUpdatedDetail {
  conversationId: string
  /** The turn's message id, so the device that produced it could skip its own echo. */
  originMessageId?: string
}

/** Called by SocketProvider on a `{type:'chat.updated'}` notification. */
export function dispatchChatUpdated(detail: ChatUpdatedDetail): void {
  if (typeof window === 'undefined' || !detail?.conversationId) return
  window.dispatchEvent(new CustomEvent<ChatUpdatedDetail>(CHAT_UPDATED_EVENT, {detail}))
}

/** Subscribed by the chat wrapper. Returns an unsubscribe function. */
export function subscribeChatUpdated(cb: (detail: ChatUpdatedDetail) => void): () => void {
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<ChatUpdatedDetail>).detail
    if (detail?.conversationId) cb(detail)
  }
  window.addEventListener(CHAT_UPDATED_EVENT, handler)
  return () => window.removeEventListener(CHAT_UPDATED_EVENT, handler)
}

/** Normalize a raw notification-socket message, or null if it isn't a chat.updated. */
export function parseChatUpdatedMessage(message: unknown): ChatUpdatedDetail | null {
  if (!message || typeof message !== 'object') return null
  const m = message as Record<string, unknown>
  if (m.type !== 'chat.updated') return null
  const conversationId = String(m.conversation_id ?? m.conversationId ?? '').trim()
  if (!conversationId) return null
  const originMessageId = String(m.origin_message_id ?? m.originMessageId ?? '').trim()
  return {conversationId, ...(originMessageId ? {originMessageId} : {})}
}
